import React from "react";
import { Box, IconButton, Typography } from "@mui/joy";
import prev from "../../assets/svgs/prevIcon.svg";
import next from "../../assets/svgs/forwardIcon.svg";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) pages.push("...");

      if (currentPage !== 1 && currentPage !== totalPages) {
        pages.push(currentPage);
      }

      if (currentPage < totalPages - 2) pages.push("...");

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
      <Typography
        level="body-sm"
        sx={{ fontSize: "13px", fontWeight: "400", color: "#7B7F85" }}
        mb={1}
      >
        {(currentPage - 1) * itemsPerPage + 1} -{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        applicants
      </Typography>

      <Box display="flex" gap={1} alignItems="center">
        <IconButton
          variant="plain"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          sx={{
            opacity: currentPage === 1 ? 0.4 : 1,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          <img src={prev} alt="Previous" />
        </IconButton>

        {getPageNumbers().map((page, idx) =>
          typeof page === "number" ? (
            <IconButton
              key={idx}
              variant={page === currentPage ? "solid" : "outlined"}
              onClick={() => onPageChange(page)}
              sx={{
                borderRadius: "8px",
                width: 40,
                height: 40,
                backgroundColor: page === currentPage ? "#2E2D7E" : "#FFFFFF",
              }}
            >
              {page}
            </IconButton>
          ) : (
            <Typography key={idx} level="body-sm" px={1}>
              ...
            </Typography>
          )
        )}

        <IconButton
          variant="plain"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          sx={{
            opacity: currentPage === totalPages ? 0.4 : 1,
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          <img src={next} alt="Next" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Pagination;
